import { ref, provide, inject, computed } from "vue";
import { useAsyncOperation } from "Admin/js/Composables/useAsyncOperation";
import axios from "axios";
import {
    WORKDAY_STATUS,
    SHIFT_STATUS,
    OFFICE_HOUR_STATUS,
} from "Admin/js/Constants/workday";

const {
    SHIFT_IN,
    SHIFT_RESUMED,
    SHIFT_OUT,
    SHIFT_PAUSED,
    SHIFT_PAUSED_UNREACHABLE,
    SHIFT_PAUSED_INACTIVE,
} = SHIFT_STATUS;

const { WORKDAY_STARTED, WORKDAY_STOPPED } = WORKDAY_STATUS;

const { OFFICE_CLOSE } = OFFICE_HOUR_STATUS;

const symbol = Symbol("mobileWorkday");

function useWorkdayState() {
    const workdayInfo = ref({
        status: null,
        date: null,
        duration: null,
    });
    const shiftInfo = ref({
        status: null,
        date: null,
        duration: null,
    });
    const isLoading = ref(false);
    const officeHourStatus = ref(OFFICE_CLOSE);

    const isWorkdayStarted = computed(() => {
        return workdayInfo.value.status === WORKDAY_STARTED;
    });
    const isWorkdayStoppedOrNotStarted = computed(() => {
        return [WORKDAY_STOPPED, null].includes(workdayInfo.value.status);
    });

    const isShiftActive = computed(() => {
        return [
            SHIFT_IN,
            SHIFT_RESUMED,
            SHIFT_PAUSED,
            SHIFT_PAUSED_UNREACHABLE,
            SHIFT_PAUSED_INACTIVE,
        ].includes(shiftInfo.value.status);
    });

    const isShiftStoppedOrNotStarted = computed(() => {
        return [SHIFT_OUT, null].includes(shiftInfo.value.status);
    });
    const isShiftPausedOrUnreachableOrInActive = computed(() => {
        return [
            SHIFT_PAUSED,
            SHIFT_PAUSED_UNREACHABLE,
            SHIFT_PAUSED_INACTIVE,
        ].includes(shiftInfo.value.status);
    });
    const shiftStartDate = computed(() => {
        const now = new Date();
        const durationInMs = (shiftInfo.value.duration || 0) * 60 * 1000;
        const startDate = new Date(now.getTime() - durationInMs);
        return startDate.toUTCString();
    });

    const { manageAsyncOperation } = useAsyncOperation();

    function fetchWorkdayData(noLoading = false) {
        return manageAsyncOperation(
            async () => {
                const { data } = await axios.post("/shift-timer/shift/status");
                const responseData = data?.data || data;
                workdayInfo.value = {
                    status: responseData?.workday_status,
                    date: responseData?.workday_started_at,
                    duration: responseData?.workday_duration_in_minutes,
                };
                shiftInfo.value = {
                    status: responseData?.shift_status,
                    date: responseData?.shift_date,
                    duration: responseData?.shift_duration_in_minutes,
                };
                if (responseData?.workday_office_status) {
                    officeHourStatus.value = responseData.workday_office_status;
                } else {
                    officeHourStatus.value = OFFICE_CLOSE;
                }
            },
            null,
            (loading) => (isLoading.value = loading),
            noLoading,
        );
    }

    async function startWorkday() {
        return manageAsyncOperation(
            async () => {
                await axios.post("/workdays/start");
                workdayInfo.value.status = WORKDAY_STARTED;
                await fetchWorkdayData();
            },
            null,
            (loading) => (isLoading.value = loading),
        );
    }
    async function endWorkday() {
        return manageAsyncOperation(
            async () => {
                await axios.post("/workdays/end");
                workdayInfo.value.status = WORKDAY_STOPPED;
                await fetchWorkdayData();
            },
            null,
            (loading) => (isLoading.value = loading),
        );
    }

    async function performShiftAction(event, newStatus) {
        return manageAsyncOperation(
            async () => {
                await axios.post("/shift-timer/shift/log", { event });
                shiftInfo.value.status = newStatus;
                await fetchWorkdayData();
            },
            null,
            (loading) => (isLoading.value = loading),
        );
    }

    function startShift() {
        return performShiftAction(SHIFT_IN, SHIFT_IN);
    }
    function pauseShift() {
        return performShiftAction(SHIFT_PAUSED, SHIFT_PAUSED);
    }
    function resumeShift() {
        return performShiftAction(SHIFT_RESUMED, SHIFT_RESUMED);
    }
    function stopShift() {
        return performShiftAction(SHIFT_OUT, SHIFT_OUT);
    }

    return {
        workdayInfo,
        shiftInfo,
        isWorkdayStarted,
        isWorkdayStoppedOrNotStarted,
        isShiftActive,
        isShiftStoppedOrNotStarted,
        isShiftPausedOrUnreachableOrInActive,
        isLoading,
        officeHourStatus,
        shiftStartDate,
        fetchWorkdayData,
        startWorkday,
        endWorkday,
        startShift,
        pauseShift,
        resumeShift,
        stopShift,
    };
}

export function provideMobileWorkday() {
    const state = useWorkdayState();
    provide(symbol, state);
    return state;
}

export function useMobileWorkday() {
    return inject(symbol);
}
